"use client";

import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";
import { FeatherX } from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  getListInvitationsQueryControllerListQueryKey,
  useCreateInvitationCommandControllerCreate,
} from "@/generated/invitations/invitations";
import { getListMembersQueryControllerListMembersQueryKey } from "@/generated/membership/membership";
import { CreateInvitationDtoRole } from "@/generated/model";

const invitationSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, "Email is required")
    .email("Please enter a valid email address"),
  role: z.enum(["MEMBER", "ADMIN"]),
});

interface InviteMemberModalProps {
  organisationSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteMemberModal({
  organisationSlug,
  open,
  onOpenChange,
}: InviteMemberModalProps) {
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<CreateInvitationDtoRole>(
    CreateInvitationDtoRole.ADMIN,
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Create invitation mutation
  const { mutate: createInvitation, isPending } =
    useCreateInvitationCommandControllerCreate({
      mutation: {
        onSuccess: async () => {
          toast.success("Invitation sent successfully");
          // Invalidate both members and invitations queries to refresh the lists
          const membersQueryKey =
            getListMembersQueryControllerListMembersQueryKey(organisationSlug);
          const invitationsQueryKey =
            getListInvitationsQueryControllerListQueryKey(organisationSlug, {});
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: membersQueryKey }),
            queryClient.invalidateQueries({ queryKey: invitationsQueryKey }),
          ]);
          onOpenChange(false);
        },
        onError: (error: unknown) => {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Failed to send invitation";
          toast.error(errorMessage);
        },
      },
    });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!open) {
      setEmail("");
      setRole(CreateInvitationDtoRole.ADMIN);
      setErrors({});
    }
  }, [open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const result = invitationSchema.safeParse({ email, role });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((err) => {
        const path = err.path.join(".");
        fieldErrors[path] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});

    createInvitation({
      organisationSlug,
      data: {
        email,
        role,
      },
    });
  };

  const roleDescriptions: Record<string, string> = {
    MEMBER: "Members can view and participate in the nursery group.",
    ADMIN: "Group Admins can manage members and nursery group settings.",
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="max-w-2xl max-h-[90vh] overflow-y-auto p-0"
      >
        <DialogTitle className="sr-only">Invite Member</DialogTitle>
        <form onSubmit={handleSubmit} className="flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-solid border-secondary-beige-200 px-6 py-4">
            <div className="flex flex-col items-start gap-1">
              <span className="text-heading-2 font-heading-2 text-default-font">
                Invite Member
              </span>
              <span className="text-body font-body text-subtext-color">
                Send an invitation to join your nursery group.
              </span>
            </div>
            <IconButton
              variant="neutral-tertiary"
              size="medium"
              icon={<FeatherX />}
              onClick={() => onOpenChange(false)}
            />
          </div>

          {/* Form Content */}
          <div className="flex flex-col gap-6 px-6 py-6">
            <TextField
              className="h-auto w-full"
              size="large"
              label="Email address"
              error={!!errors.email}
              helpText={
                errors.email ||
                "The email address of the person you want to invite"
              }
            >
              <TextField.Input
                type="email"
                placeholder="john.doe@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isPending}
              />
            </TextField>

            <div className="flex w-full items-center gap-6">
              <Select
                className="h-auto grow shrink-0 basis-0 max-w-[49%]"
                label="Role"
                placeholder="Select a role"
                size="large"
                value={role}
                onValueChange={(value) =>
                  setRole(value as CreateInvitationDtoRole)
                }
                error={!!errors.role}
                helpText={errors.role}
                disabled={isPending}
              >
                <Select.Item value={CreateInvitationDtoRole.ADMIN}>
                  Group Admin
                </Select.Item>
              </Select>
              <div className="flex h-10 flex-col items-start justify-end">
                <span className="text-body font-body text-subtext-color">
                  {roleDescriptions[role]}
                </span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 border-t border-solid border-secondary-beige-200 px-6 py-4">
            <Button
              type="button"
              variant="neutral-primary"
              size="large"
              onClick={() => onOpenChange(false)}
              disabled={isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="brand-primary"
              size="large"
              disabled={isPending}
            >
              {isPending ? "Sending..." : "Send Invitation"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
